import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/hooks/use-currency";
import { formatMinor } from "@/lib/pricing/currency";
import { useFxRates } from "@/hooks/useFxRates";

interface Props {
  sku: string;
  className?: string;
  fallbackEur?: number;
}

export function PriceBadge({ sku, className, fallbackEur }: Props) {
  const { localCurrency: currency, baseCurrency } = useCurrency();
  const rates = useFxRates();
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const { data, isLoading } = useQuery({
    queryKey: ["sku_prices", sku],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sku_prices")
        .select("currency, amount_minor")
        .eq("sku", sku);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return <span className={className} data-price data-currency={currency}>…</span>;
  }

  // Always anchor to EUR (Stripe charging currency); show local approximation
  // when the user's chosen currency differs. Mirrors `<Price>` so all surfaces
  // render consistently.
  const eurRow = data?.find((r) => r.currency === baseCurrency);
  const eurMinor = eurRow?.amount_minor ?? Math.round((fallbackEur ?? 0) * 100);
  if (!eurMinor) return <span className={className} data-price data-currency={currency}>—</span>;

  const eurFormatted = formatMinor(eurMinor, baseCurrency, locale);

  if (currency === baseCurrency) {
    return (
      <span className={className} data-price data-currency={baseCurrency} data-eur-cents={eurMinor}>
        {eurFormatted}
      </span>
    );
  }

  // Prefer an explicit SKU row in the chosen currency over an FX conversion.
  const localRow = data?.find((r) => r.currency === currency);
  const localMinor = localRow
    ? localRow.amount_minor
    : Math.round((eurMinor / 100) * (rates[currency] ?? 1) * 100);
  const localFormatted = formatMinor(localMinor, currency, locale);

  return (
    <span className={className} data-price data-currency={baseCurrency} data-eur-cents={eurMinor}>
      {eurFormatted}
      <span className="ml-1 text-muted-foreground" aria-label="approximate local price">
        · {localFormatted}
      </span>
    </span>
  );
}